import { apiClient, unwrap } from '@services/api';
import type { AuthTokens, User, UserRole } from '@/types';

export interface LoginPayload {
  username: string;
  password: string;
  role: UserRole;
}

export interface LoginResponse {
  user: User;
  tokens: AuthTokens;
}

export const authService = {
  async login(payload: LoginPayload): Promise<LoginResponse> {
    const res = await apiClient.post<LoginResponse>('/auth/login', payload);
    return unwrap(res);
  },

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const res = await apiClient.post<AuthTokens>('/auth/refresh', { refresh_token: refreshToken });
    return unwrap(res);
  },

  async getProfile(): Promise<User> {
    const res = await apiClient.get<User>('/auth/me');
    return unwrap(res);
  },

  async forgotPassword(username: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { username });
  },

  async verifyResetCode(username: string, code: string): Promise<{ valid: boolean }> {
    const res = await apiClient.post<{ valid: boolean }>('/auth/verify-reset-code', {
      username,
      code,
    });
    return unwrap(res);
  },

  async resetPassword(username: string, code: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { username, code, newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', { currentPassword, newPassword });
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const res = await apiClient.patch<User>('/auth/profile', data);
    return unwrap(res);
  },
};
